import React, { useState } from 'react';
import styled from 'styled-components';
import { withTheme } from 'styled-components';
import { Search as SearchIcon, PlayCircle as PlayCircleIcon } from 'react-feather';
import { useAppContext } from 'containers/AppProvider';
// @ts-expect-error - Vite SVG import typing issue
import { ReactComponent as Logo } from '../assets/mosaic_icon.svg';
import {
  InputBase,
  AppBar as MuiAppBar,
  Button as MuiButton,
  IconButton as MuiIconButton,
  Toolbar,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';

import { Menu as MenuIcon } from '@mui/icons-material';

import UserDropdown from './UserDropdown';

import {
  KEYBOARD_KEYS,
  REPORTS_TABLE_TEAMS_V2_PATH,
  REPORTS_TABLE_MEMBERS_PATH,
  DEMO_ACCOUNT,
  DEMO_ACCOUNT_SHORTCUT_LABEL,
} from '@constants';
import { useNavigate } from 'react-router-dom';
import { ToggleOptionsButton } from 'components/ToggleOptionsButton';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { getTeamProfileUrl } from 'utils/url';

const AppBar = styled(MuiAppBar)`
  background: ${(props: any) =>
    props.theme.palette.mode === 'dark'
      ? props.theme.sidebar.header.background
      : 'rgba(255, 255, 255, 0.8)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: ${(props: any) => props.theme.palette.text.primary};
  border-bottom: 1px solid
    ${(props: any) =>
      props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'};
  box-shadow: none;

  /* Improve rendering performance during scroll */
  will-change: transform;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);

  & .MuiToolbar-root {
    min-height: 64px;
    padding: ${(props: any) => props.theme.spacing(1.5, 2)};
    display: flex;
    align-items: center;
    gap: ${(props: any) => props.theme.spacing(1.5)};
    flex-wrap: nowrap;

    ${(props: any) => props.theme.breakpoints.down('md')} {
      padding: ${(props: any) => props.theme.spacing(1, 1.5)};
      gap: ${(props: any) => props.theme.spacing(1)};
    }
  }
`;

const LogoContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${(props: any) => props.theme.spacing(1.5)};
  cursor: pointer;
  padding: ${(props: any) => props.theme.spacing(0.5, 1)};
  border-radius: 8px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${(props: any) =>
      props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};

    svg {
      animation: spin3d 0.6s ease-in-out;
    }
  }

  svg {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    transition: transform 0.3s ease;
    will-change: transform;
  }

  @keyframes spin3d {
    0% {
      transform: perspective(600px) rotateY(0deg) rotateX(0deg);
    }
    50% {
      transform: perspective(600px) rotateY(180deg) rotateX(20deg) scale(1.1);
      filter: drop-shadow(0 0 8px rgba(251, 140, 0, 0.6));
    }
    100% {
      transform: perspective(600px) rotateY(360deg) rotateX(0deg);
    }
  }

  /* Respect user's motion preferences */
  @media (prefers-reduced-motion: reduce) {
    svg {
      animation: none !important;
      transition: none;
    }

    &:hover svg {
      animation: none !important;
    }
  }
`;

const IconButton = styled(MuiIconButton)`
  svg {
    width: 22px;
    height: 22px;
  }
`;

const SearchContainer = styled.div`
  border-radius: 8px;
  border: 1px solid ${(props: any) => props.theme.palette.divider};
  background: ${(props: any) =>
    props.theme.palette.mode === 'dark' ? 'rgba(35, 48, 68, 0.6)' : 'rgba(247, 249, 252, 0.8)'};
  position: relative;
  width: 100%;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(props: any) => props.theme.palette.action.disabled};
  }

  &:focus-within {
    border-color: ${(props: any) => props.theme.palette.primary.main};
    background: ${(props: any) =>
      props.theme.palette.mode === 'dark' ? 'rgba(35, 48, 68, 0.8)' : 'rgba(255, 255, 255, 0.9)'};
  }
`;

const SearchIconWrapper = styled.div`
  width: 36px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props: any) => props.theme.palette.text.secondary};
  left: 0;

  ${(props: any) => props.theme.breakpoints.up('md')} {
    width: 40px;
  }

  svg {
    width: 18px;
    height: 18px;

    ${(props: any) => props.theme.breakpoints.up('md')} {
      width: 20px;
      height: 20px;
    }
  }
`;

const Input = styled(InputBase)`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props: any) => props.theme.palette.text.primary};
    padding: 8px 12px 8px 36px;
    width: 100%;
    min-width: 0;
    font-size: 0.875rem;
    font-weight: 400;

    ${(props: any) => props.theme.breakpoints.up('md')} {
      padding: 10px 16px 10px 40px;
      font-size: 0.9375rem;
    }

    &::placeholder {
      color: ${(props: any) =>
        props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)'};
      opacity: 1;
    }
  }
`;

const DemoShortcutButton = styled(MuiButton)`
  white-space: nowrap;
  text-transform: none;
  font-weight: 600;
  border-color: ${(props: any) => props.theme.palette.divider};
  color: ${(props: any) => props.theme.palette.text.secondary};

  &:hover {
    border-color: ${(props: any) => props.theme.palette.action.disabled};
    background: ${(props: any) =>
      props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const BrandText = styled(Typography)`
  font-size: 1.125rem;
  font-weight: 700;
  color: ${(props: any) => props.theme.palette.text.primary};
  font-family: ${(props: any) => props.theme.typography.fontFamily};
  white-space: nowrap;
`;

type AppBarProps = {
  theme?: Record<string, unknown>;
  onDrawerToggle: React.MouseEventHandler<HTMLElement>;
};

const AppBarComponent: React.FC<AppBarProps> = ({ onDrawerToggle }) => {
  const navigate = useNavigate();
  const { searchBarInputRef } = useAppContext();
  const { canJumpToDemoAccount } = useFeatureFlags();
  const [searchEntity, setSearchEntity] = useState<'team' | 'member'>('team');
  const [randomSpin, setRandomSpin] = useState(false);

  // Random animation trigger - fires occasionally
  React.useEffect(() => {
    // Respect user's motion preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    let timeoutId: NodeJS.Timeout | null = null;

    const triggerRandomSpin = () => {
      const shouldSpin = Math.random() < 0.15; // 15% chance
      if (shouldSpin) {
        setRandomSpin(true);
        timeoutId = setTimeout(() => {
          setRandomSpin(false);
          timeoutId = null;
        }, 800);
      }
    };

    // Check every 8-15 seconds
    const interval = setInterval(triggerRandomSpin, 8000 + Math.random() * 7000);

    return () => {
      clearInterval(interval);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <AppBar position="fixed" elevation={0}>
      <Toolbar>
        {/* Mobile Menu Button */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, mr: 1 }}>
          <IconButton color="inherit" aria-label="Open drawer" onClick={onDrawerToggle}>
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Logo & Brand - Only show on mobile when sidebar is hidden */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
          <LogoContainer onClick={() => navigate('/')}>
            <Logo style={{ animation: randomSpin ? 'spin3d 0.6s ease-in-out' : 'none' }} />
            <BrandText>Galaxy</BrandText>
          </LogoContainer>
        </Box>

        {/* Spacer to account for sidebar on desktop */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, width: 260 }} />

        {/* Search Bar with Toggle - Expands to use available space */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            flex: '1 1 auto',
            maxWidth: { sm: '500px', md: '700px', lg: '900px' },
            minWidth: 0,
            mx: 2,
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          {/* Team/Member Toggle */}
          <Box sx={{ flexShrink: 0 }}>
            <ToggleOptionsButton
              options={[
                { label: 'Team', value: 'team' },
                { label: 'Member', value: 'member' },
              ]}
              currentOption={searchEntity}
              onSelect={({ option }) => setSearchEntity(option.value as 'team' | 'member')}
            />
          </Box>

          {/* Search Input */}
          <SearchContainer>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <Input
              placeholder="Search all tenants..."
              onKeyDown={(e) => {
                const { key } = e;
                if (key === KEYBOARD_KEYS.ENTER) {
                  const search_text = searchBarInputRef?.current?.value?.trim();
                  if (search_text) {
                    const path =
                      searchEntity === 'member'
                        ? REPORTS_TABLE_MEMBERS_PATH
                        : REPORTS_TABLE_TEAMS_V2_PATH;
                    navigate(`${path}?q=${encodeURIComponent(search_text)}`);
                  }
                }
              }}
              inputRef={searchBarInputRef}
            />
          </SearchContainer>
        </Box>

        {/* Right Side Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 'auto' }}>
          {/* Prod-only shortcut to the shared demo account Sales demos from */}
          {canJumpToDemoAccount && (
            <Tooltip title={`Open the demo team (${DEMO_ACCOUNT.realmId} realm)`}>
              <DemoShortcutButton
                size="small"
                variant="outlined"
                startIcon={<PlayCircleIcon />}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                onClick={() =>
                  navigate(
                    getTeamProfileUrl({
                      teamId: DEMO_ACCOUNT.teamId,
                      tenant: DEMO_ACCOUNT.tenant,
                      realmId: DEMO_ACCOUNT.realmId,
                    }),
                  )
                }
              >
                {DEMO_ACCOUNT_SHORTCUT_LABEL}
              </DemoShortcutButton>
            </Tooltip>
          )}
          <UserDropdown />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default withTheme(AppBarComponent);
